/**
 * Intent Router
 * Maps classified intents to specific actions in the app
 *
 * This replaces brittle regex patterns with a clean, extensible architecture:
 * 1. User message → LLM classifies intent
 * 2. Intent → Router determines action
 * 3. Action → Handler executes the response
 *
 * Auth bridge: call configureIntentRouterApi(fn) to inject a platform-specific
 * API caller (e.g., one that adds Bearer tokens). Falls back to plain fetch.
 */

// Auth bridge: platform-specific API caller (injected by web/mobile)
let _callApi = null;

/**
 * Configure a custom API caller for authenticated requests.
 * @param {Function} fn - async (url, options) => Response
 */
export function configureIntentRouterApi(fn) {
  _callApi = fn;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

// Auth headers helper
// When _callApi is configured (web/mobile), it handles auth headers itself,
// so we skip the dynamic import of supabase which can hang in some environments.
const getApiHeaders = async () => {
  const headers = { 'Content-Type': 'application/json' };
  if (_callApi) return headers; // _callApi adds auth headers — skip duplicate getSession()
  try {
    const { getSession } = await import('../lib/supabase');
    const session = await getSession();
    if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;
  } catch { /* proceed without auth */ }
  return headers;
};

// ============================================
// INTENT CLASSIFICATION
// ============================================

/**
 * Classifies user intent using the LLM endpoint
 * @param {string} message - The user's message
 * @param {Array} conversationHistory - Recent messages for context
 * @param {Object} currentState - Current app state (selected destinations, etc.)
 * @param {string} tripType - 'destination' or 'hotel'
 * @returns {Promise<Object>} - Classified intent with extract data
 */
export async function classifyIntent(message, conversationHistory = [], currentState = {}, tripType = 'destination') {
  try {
    const apiUrl = `${BACKEND_URL}/api/classify-intent`;
    const apiOptions = {
      method: 'POST',
      headers: await getApiHeaders(),
      body: JSON.stringify({
        message,
        conversationHistory,
        currentState,
        tripType
      })
    };
    const response = await (_callApi || fetch)(apiUrl, apiOptions);

    if (!response.ok) {
      throw new Error(`Intent classification failed: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Intent classification error:', error);
    // Return unknown intent on error - system will handle conversationally
    return {
      intent: 'unknown',
      confidence: 'low',
      extract: null,
      reasoning: 'Classification request failed',
      error: error.message
    };
  }
}

// ============================================
// INTENT HANDLERS
// ============================================

/**
 * Handler configuration for each intent type
 * Each handler returns an action object that tells the app what to do
 */
const intentHandlers = {

  /**
   * User selected specific destination(s)
   * Action: Add to trip builder, show destination cards
   * Also handles: hotel preferences, resort selections, mixed trips
   */
  destination_selection: (extract, context) => {
    let destinations = extract?.destinations || [];
    const hotelPreference = extract?.hotelPreference || extract?.hotel || null;

    // Detect vague items like "beach resort", "a ski destination", "somewhere warm"
    const vaguePatterns = [
      'beach resort', 'mountain resort', 'ski resort', 'luxury resort', 'spa resort',
      'ski destination', 'ski spot', 'ski town', 'ski area', 'skiing',
      'beach destination', 'beach town', 'beach spot',
      'somewhere warm', 'somewhere cold', 'somewhere tropical', 'somewhere relaxing',
      'a resort', 'a beach', 'a mountain',
      // Activity-based vague destinations
      'destination with', 'destination for', 'destination near',
      'scuba diving', 'snorkeling', 'surfing', 'hiking', 'diving',
      'another destination', 'a destination',
    ];
    const vagueItems = destinations.filter(d =>
      vaguePatterns.some(pattern => d.toLowerCase().includes(pattern))
    );
    const validDestinations = destinations.filter(d =>
      !vaguePatterns.some(pattern => d.toLowerCase().includes(pattern))
    );

    const hasVagueItems = vagueItems.length > 0;
    destinations = validDestinations;

    if (destinations.length === 0) {
      // All items were vague - treat as recommendation request
      if (hasVagueItems) {
        return {
          action: 'search_with_preferences',
          preferences: extract?.destinations || [],
          vagueItems,
          message: "I'd love to help you find the perfect destination! Let me ask a few questions to narrow down the options.",
          triggerRecommendation: true
        };
      }
      return {
        action: 'ask_clarification',
        message: "I'd love to help you plan a trip! Which destination are you thinking about?"
      };
    }

    // Build response message
    let message;
    if (destinations.length === 1) {
      message = `Great choice! I'll help you plan your trip to **${destinations[0]}**.`;
      if (hotelPreference) {
        message += ` I've noted your preference for **${hotelPreference}**.`;
      }
    } else {
      message = `Excellent! I'll help you plan a multi-stop trip to **${destinations.join('**, **')}**.`;
      if (hotelPreference) {
        message += ` I've noted your preference for **${hotelPreference}**.`;
      }
    }

    return {
      action: destinations.length === 1 ? 'select_destination' : 'select_multiple_destinations',
      destinations,
      vagueItems: hasVagueItems ? vagueItems : undefined,
      hotelPreference,
      message,
      addToTripBuilder: destinations.length > 1,
      showDestinationCard: destinations.length === 1,
      showMultiStopBuilder: destinations.length > 1,
      // Pass through trip metadata from LLM extraction
      duration: extract?.duration || null,
      travelers: extract?.travelers || null,
      occasion: extract?.occasion || null,
      month: extract?.month || null,
      preferences: extract?.preferences || null,
    };
  },

  /**
   * User wants recommendations
   * Action: Show quiz or ask clarifying questions
   */
  recommendation_request: (extract, context) => {
    const preferences = extract?.preferences || [];

    if (preferences.length === 0) {
      return {
        action: 'start_recommendation_flow',
        message: "I'd love to help you find the perfect destination! Let me ask you a few questions to understand what you're looking for.",
        showQuiz: true
      };
    }

    return {
      action: 'search_with_preferences',
      preferences,
      message: `Looking for destinations that match: **${preferences.join('**, **')}**. Let me find some options for you!`,
      triggerRecommendation: true
    };
  },

  /**
   * User is correcting/changing their selection
   * Action: Remove old, add new
   */
  correction: (extract, context) => {
    const toRemove = extract?.remove;
    const toAdd = extract?.add;

    if (!toAdd) {
      return {
        action: 'ask_clarification',
        message: "I understand you want to change your selection. What destination would you like instead?"
      };
    }

    return {
      action: 'correct_selection',
      remove: toRemove,
      add: toAdd,
      message: toRemove
        ? `Got it! I've replaced **${toRemove}** with **${toAdd}**.`
        : `Got it! I've updated your selection to **${toAdd}**.`,
      updateTripBuilder: true
    };
  },

  /**
   * User wants to add destination(s) to existing trip
   * Action: Add to trip builder
   */
  add_to_trip: (extract, context) => {
    if (extract?.addAll) {
      return {
        action: 'add_all_suggestions',
        message: "Perfect! I've added all the suggested destinations to your trip.",
        addAllSuggestions: true
      };
    }

    const destinations = extract?.destinations || [];

    if (destinations.length === 0) {
      return {
        action: 'ask_clarification',
        message: "Which destination would you like to add to your trip?"
      };
    }

    return {
      action: 'add_destinations',
      destinations,
      message: `Added **${destinations.join('**, **')}** to your trip!`,
      updateTripBuilder: true
    };
  },

  /**
   * User wants to remove destination(s)
   * Action: Remove from trip builder
   */
  remove_from_trip: (extract, context) => {
    const destinations = extract?.destinations || [];

    if (destinations.length === 0) {
      return {
        action: 'ask_clarification',
        message: "Which destination would you like to remove from your trip?"
      };
    }

    return {
      action: 'remove_destinations',
      destinations,
      message: `Removed **${destinations.join('**, **')}** from your trip.`,
      updateTripBuilder: true
    };
  },

  /**
   * User confirmed something
   * Action: Proceed with pending action
   */
  confirmation: (extract, context) => {
    // Check what's pending in context
    if (context.pendingAction) {
      return {
        action: 'execute_pending',
        pendingAction: context.pendingAction,
        message: "Perfect! Let's proceed."
      };
    }

    // If we have destinations selected, proceed to itinerary
    if (context.tripBuilderStops?.length > 0) {
      return {
        action: 'proceed_to_itinerary',
        message: "Great! Let me start creating your itinerary."
      };
    }

    return {
      action: 'ask_clarification',
      message: "Sounds good! What would you like to do next?"
    };
  },

  /**
   * User rejected/declined something
   * Action: Ask what they want instead
   */
  rejection: (extract, context) => {
    return {
      action: 'handle_rejection',
      message: "No problem! Would you like me to show you different options, or do you have something else in mind?",
      clearPending: true
    };
  },

  /**
   * User is asking a question
   * Action: Provide information
   */
  question: (extract, context) => {
    const topic = extract?.topic;
    const questionType = extract?.questionType;

    return {
      action: 'answer_question',
      topic,
      questionType,
      message: null, // Will be generated by LLM
      needsLLMResponse: true
    };
  },

  /**
   * User wants to modify itinerary
   * Action: Apply modification
   */
  modification: (extract, context) => {
    return {
      action: 'modify_itinerary',
      target: extract?.target,
      modificationAction: extract?.action,
      details: extract?.details,
      message: "I'll help you make that change.",
      needsLLMResponse: true
    };
  },

  /**
   * User said hello
   * Action: Greet back
   */
  greeting: (extract, context) => {
    const greetings = [
      "Hello! 👋 Ready to plan an amazing trip?",
      "Hi there! Where would you like to explore?",
      "Hey! I'm excited to help you plan your next adventure!",
      "Hello! What destination has been on your mind lately?"
    ];

    return {
      action: 'greet',
      message: greetings[Math.floor(Math.random() * greetings.length)]
    };
  },

  /**
   * User said goodbye/thanks
   * Action: Friendly farewell
   */
  farewell: (extract, context) => {
    const farewells = [
      "You're welcome! Have an amazing trip! ✈️",
      "Happy travels! Come back anytime you need help planning!",
      "Enjoy your adventure! 🌍",
      "Thanks for using Wander! Safe travels!"
    ];

    return {
      action: 'farewell',
      message: farewells[Math.floor(Math.random() * farewells.length)]
    };
  },

  /**
   * Unknown intent - handle conversationally
   * Action: Send to LLM for natural response
   */
  unknown: (extract, context) => {
    return {
      action: 'conversational_response',
      message: null, // Will be generated by LLM
      needsLLMResponse: true,
      guidancePrompt: "Respond helpfully but try to guide the conversation back to travel planning."
    };
  }
};

// ============================================
// MAIN ROUTER
// ============================================

/**
 * Routes a classified intent to the appropriate handler
 * @param {Object} classifiedIntent - The result from classifyIntent()
 * @param {Object} context - Current app context (selected destinations, pending actions, etc.)
 * @returns {Object} - Action object describing what the app should do
 */
export function routeIntent(classifiedIntent, context = {}) {
  const { intent, extract, confidence } = classifiedIntent;

  // Get the handler for this intent
  const handler = intentHandlers[intent] || intentHandlers.unknown;

  // Execute the handler
  const action = handler(extract, context);

  // Attach metadata
  return {
    ...action,
    originalIntent: intent,
    confidence,
    extract
  };
}

// ============================================
// CONVENIENCE FUNCTION
// ============================================

/**
 * One-shot function to classify and route in a single call
 * @param {string} message - User's message
 * @param {Array} conversationHistory - Recent messages
 * @param {Object} currentState - Current app state
 * @param {string} tripType - 'destination' or 'hotel'
 * @returns {Promise<Object>} - Action object
 */
export async function processUserMessage(message, conversationHistory = [], currentState = {}, tripType = 'destination') {
  // Step 1: Classify the intent
  const classifiedIntent = await classifyIntent(message, conversationHistory, currentState, tripType);

  // Step 2: Route to get action
  const action = routeIntent(classifiedIntent, {
    ...currentState,
    conversationHistory
  });

  return action;
}

// ============================================
// NEW: STRUCTURED EXTRACTION SYSTEM
// ============================================

/**
 * Calls the new /api/extract-message endpoint for structured extraction
 * @param {string} message - User's message
 * @param {Array} conversationHistory - Recent messages for context
 * @param {Object} tripContext - Accumulated trip context
 * @param {string} tripType - 'destination' or 'hotel'
 * @returns {Promise<Object>} - Unified extraction object
 */
export async function extractMessage(message, conversationHistory = [], tripContext = {}, tripType = 'destination') {
  try {
    const apiUrl = `${BACKEND_URL}/api/extract-message`;
    const apiOptions = {
      method: 'POST',
      headers: await getApiHeaders(),
      body: JSON.stringify({ message, conversationHistory, tripContext, tripType })
    };
    const response = await (_callApi || fetch)(apiUrl, apiOptions);

    if (!response.ok) {
      throw new Error(`Extraction failed: ${response.status}`);
    }

    const result = await response.json();

    // Capture requestId from backend for bug report correlation
    const requestId = response.headers.get('X-Request-Id');
    if (requestId) {
      result._requestId = requestId;
    }

    return result;
  } catch (error) {
    console.error('Extraction error:', error);
    return {
      action: 'social', destinations: [], removals: [],
      duration: null, travelers: null, occasion: null, month: null,
      activities: [], preferences: [], requestedPlaces: [],
      question: null, modification: null,
      sentiment: 'neutral', hotelPreference: null, addAll: false
    };
  }
}

/**
 * Normalize accented characters for comparison (e.g., "Pinhão" → "pinhao")
 * Prevents mismatches when LLM uses different accent forms for the same city.
 */
function normalizeForComparison(str) {
  return (str || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
}

/**
 * Merges a new extraction into the accumulated trip context
 * Scalar fields: new value overwrites, null preserves old
 * Array fields: union/deduplicate (always grows)
 */
export function mergeTripContext(prevContext, extraction) {
  // Build new specific destinations to add — filter out originCity (departure point, not a destination)
  // Use accent-normalized comparison so "Pinhão" (origin) matches "Pinhao" (destination)
  const originNorm = normalizeForComparison(extraction.originCity);
  const newSpecific = (extraction.destinations || [])
    .filter(d => d.specific)
    .filter(d => !originNorm || normalizeForComparison(d.name) !== originNorm)
    .map(d => d.name);

  // Build new vague destinations to add
  const newVague = (extraction.destinations || [])
    .filter(d => !d.specific)
    .map(d => ({ description: d.name, resolvedOptions: [], selected: null }));

  // Apply removals to existing destinations
  const removals = (extraction.removals || []).map(r => r.toLowerCase());
  const filteredDestinations = (prevContext.destinations || [])
    .filter(d => !removals.some(r => d.toLowerCase().includes(r) || r.includes(d.toLowerCase())));

  return {
    destinations: [...filteredDestinations, ...newSpecific],
    pendingVague: [...(prevContext.pendingVague || []), ...newVague],

    // Scalar fields: new value overwrites, null preserves old
    duration: extraction.duration ?? prevContext.duration,
    travelers: extraction.travelers ?? prevContext.travelers,
    occasion: extraction.occasion ?? prevContext.occasion,
    month: extraction.month ?? prevContext.month,
    hotelPreference: extraction.hotelPreference ?? prevContext.hotelPreference,
    originCity: extraction.originCity ?? prevContext.originCity ?? null,
    arrivalTime: extraction.arrivalTime ?? prevContext.arrivalTime ?? null,

    // Array fields: union (deduplicated)
    activities: [...new Set([...(prevContext.activities || []), ...(extraction.activities || [])])],
    preferences: [...new Set([...(prevContext.preferences || []), ...(extraction.preferences || [])])],
    requestedPlaces: [...new Set([...(prevContext.requestedPlaces || []), ...(extraction.requestedPlaces || [])])],
  };
}

/**
 * Determines which actions to take based on extracted data
 * Returns an array of action descriptors — one message can trigger multiple actions
 */
export function determineActions(extraction, tripContext) {
  const actions = [];

  // Handle removals first — filter out vague pronoun removals that aren't actual destination names
  // e.g., "I don't like these" may produce removals: ["these"] which won't match any stop
  const VAGUE_REMOVALS = new Set(['these', 'those', 'them', 'it', 'all', 'everything', 'the ones', 'the places', 'this', 'that']);
  const concreteRemovals = (extraction.removals || []).filter(r => !VAGUE_REMOVALS.has(r.toLowerCase().trim()));
  if (concreteRemovals.length > 0) {
    actions.push({ type: 'remove_stops', targets: concreteRemovals });
  }

  // Handle specific destinations — filter out originCity (departure point, not a destination)
  // Use accent-normalized comparison so "Pinhão" (origin) matches "Pinhao" (destination)
  const originNorm = normalizeForComparison(extraction.originCity);
  const specificDests = (extraction.destinations || [])
    .filter(d => d.specific)
    .filter(d => !originNorm || normalizeForComparison(d.name) !== originNorm);
  if (specificDests.length > 0) {
    actions.push({
      type: 'add_stops',
      destinations: specificDests.map(d => d.name),
      destinationDetails: specificDests, // Pass full objects with per-stop days
    });
  }

  // Handle vague destinations — always use recommend for rich numbered format
  // Previously this used a separate "resolve_vague" handler (Haiku, bare JSON array)
  // for cases with existing context, but that gave users plain buttons with no details.
  // Now we always use recommend (Sonnet, detailed numbered list with Add/Learn More buttons).
  //
  // Bug 1 fix: carry prior tripContext.preferences and tripContext.activities
  // forward into the recommend action. Otherwise refinements like
  // "tropical villa with pool/ocean views" → "somewhere in Africa" lose
  // the original constraints and the recommender returns Cape Town city
  // hotels (which violate "tropical"). The new turn's preferences are
  // ADDITIVE — we union with prior context, dedupe, and never replace.
  const priorPreferences = Array.isArray(tripContext?.preferences) ? tripContext.preferences : [];
  const priorActivities = Array.isArray(tripContext?.activities) ? tripContext.activities : [];
  const vagueDests = (extraction.destinations || []).filter(d => !d.specific);
  if (vagueDests.length > 0) {
    actions.push({
      type: 'recommend',
      preferences: [...new Set([
        ...priorActivities,
        ...priorPreferences,
        ...(extraction.activities || []),
        ...(extraction.preferences || []),
        ...vagueDests.map(d => d.name),
      ])],
      descriptions: vagueDests.map(d => d.name)
    });
  }

  // If user has activities/preferences and no recommend action yet, trigger recommend.
  // This handles messages like "I want food and wine destinations" where destinations=[]
  // but activities=["food","wine"]. Works regardless of whether the LLM classified the
  // action as plan_trip, question, or add_stop — all three indicate travel intent.
  // Also works when user already has destinations (they may want to add complementary stops).
  // NOTE: We check !hasRecommendAction instead of actions.length === 0 because a message
  // like "wedding in Pinhão, want to surf" may produce add_stops (for "Pinhão") AND should
  // also trigger recommend (for the surfing preference). The old actions.length === 0 check
  // blocked recommend whenever add_stops fired first.
  const hasPreferences = (extraction.activities?.length > 0) || (extraction.preferences?.length > 0);
  const isRecommendIntent = ['plan_trip', 'question', 'add_stop'].includes(extraction.action);
  const alreadyHasRecommend = actions.some(a => a.type === 'recommend');

  if (!alreadyHasRecommend && hasPreferences && isRecommendIntent) {
    // Bug 1 fix: union with prior tripContext preferences so refinements
    // accumulate constraints rather than replacing them.
    actions.push({
      type: 'recommend',
      preferences: [...new Set([
        ...priorActivities,
        ...priorPreferences,
        ...(extraction.activities || []),
        ...(extraction.preferences || []),
      ])],
      descriptions: []
    });
  }

  // Handle "add all" (user selecting all suggestions)
  // But first: detect "give me more" / "show me more" / "N more" patterns — user wants
  // additional recommendations, not to add all current suggestions to their trip.
  // e.g., "I love these give me 5 more" → should trigger recommend, not add_all.
  const MORE_PATTERN = /\b(give|show|find|get|suggest)\b.*\b(more|additional|other|different)\b|\b\d+\s+more\b/i;
  const originalMessage = extraction._originalMessage || '';
  if (extraction.addAll && MORE_PATTERN.test(originalMessage)) {
    // Override: user wants more recommendations, not to add all current suggestions
    extraction.addAll = false;
    if (!actions.some(a => a.type === 'recommend')) {
      actions.push({
        type: 'recommend',
        preferences: [...(extraction.activities || []), ...(extraction.preferences || [])],
        descriptions: []
      });
    }
  }

  if (extraction.addAll) {
    actions.push({ type: 'add_all' });
  }

  // Handle modifications — but first check if it's actually a geographic
  // refinement that the LLM mis-classified as "modify" (defense-in-depth
  // for prompt v8 rule 13). When the user says "I want this in Asia" after
  // an earlier search, older LLM responses sometimes still flag it as a
  // modification with a region in target/value. Promote those to a
  // recommend action that carries forward prior preferences.
  const GEO_REFINEMENT_PATTERN = /\b(asia|europe|africa|americas?|north america|south america|central america|caribbean|oceania|australia|antarctica|middle east|scandinavia|mediterranean|southeast asia|south asia|east asia|west africa|east africa|north africa|southern africa|patagonia|nordic|balkans?|alps|himalayas?|polynesia|melanesia|micronesia|tropics?|tropical|arctic|baltic|adriatic|iberia|maghreb|levant|gulf|pacific|atlantic|indian ocean|pacific islands?)\b/i;
  if (extraction.modification) {
    const modText = `${extraction.modification.target || ''} ${extraction.modification.value || ''}`.trim();
    const isGeoRefinement = modText && GEO_REFINEMENT_PATTERN.test(modText);
    const recommendAlreadyQueued = actions.some(a => a.type === 'recommend');
    if (isGeoRefinement && !recommendAlreadyQueued) {
      // Build a single vague destination that fuses prior search context
      // with the new geographic constraint, then route to recommend.
      const region = (modText.match(GEO_REFINEMENT_PATTERN) || [])[0] || modText;
      const concept = [...priorPreferences, ...priorActivities].slice(0, 3).join(' ').trim();
      const desc = concept
        ? `${concept} destination in ${region}`
        : `destination in ${region}`;
      actions.push({
        type: 'recommend',
        preferences: [...new Set([
          ...priorActivities,
          ...priorPreferences,
          ...(extraction.activities || []),
          ...(extraction.preferences || []),
          region,
        ])],
        descriptions: [desc]
      });
    } else if (!isGeoRefinement) {
      actions.push({ type: 'modify', modification: extraction.modification });
    }
    // else: geo refinement but recommend already queued — drop the modify;
    // the recommend will handle the user's intent.
  }

  // Handle questions — but not when recommend is already handling the request.
  // When the LLM extracts both preferences and a question from "I want food and wine
  // destinations", recommend should take priority (richer format with Add buttons).
  const hasRecommendAction = actions.some(a => a.type === 'recommend');
  if (extraction.question && !hasRecommendAction) {
    actions.push({ type: 'answer_question', question: extraction.question });
  }

  // Handle social responses (greeting, farewell, confirmation, rejection)
  if (extraction.action === 'social' && actions.length === 0) {
    actions.push({
      type: 'social_response',
      sentiment: extraction.sentiment || 'neutral',
      socialType: extraction._socialType || null
    });
  }

  // Fallback: if nothing determined, go conversational
  if (actions.length === 0) {
    actions.push({ type: 'conversational' });
  }

  return actions;
}

// ============================================
// EXPORTS
// ============================================

export default {
  classifyIntent,
  routeIntent,
  processUserMessage,
  // New extraction system
  extractMessage,
  mergeTripContext,
  determineActions
};
